import { TablePagination } from '@mui/material';
import type { DataGridProps, GridColDef } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import { withDefault } from '@weft/utils';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { usePageParam } from '../../../../hooks/search-params/use-page-param';
import { usePageSizeParam } from '../../../../hooks/search-params/use-page-size-param';
import { EmptyTableOverlay } from '../../../design/EmptyTableOverlay';

export type VyTableProps<Item extends Omit<GridValidRowModel, 'rows'>> = {
  itemsCount: number;
  items: Item[];
  loadingItems?: boolean;
  columns: GridColDef<Item>[];
  showPagination?: boolean;
  paginationClassName?: string;
  selfManagedPagination?: boolean;
  dontUseGlobalState?: boolean
} & Omit<DataGridProps<Item>, 'rows' | 'columns'>;

export type VyTableMinimalProps<Item extends Omit<GridValidRowModel, 'rows'>> = Omit<VyTableProps<Item>, 'rows' | 'columns'>;

export function VyTable<Item extends Omit<GridValidRowModel, 'rows'>>({
  itemsCount,
  items,
  loadingItems = false,
  columns,
  showPagination = true,
  dontUseGlobalState,
  ...props
}: VyTableProps<Item>) {
  const useParamsState = (
    globalHook: (() => ReturnType<typeof usePageParam> ) | (() => ReturnType<typeof usePageSizeParam>),
    localHook: typeof useState<number>,
    defaultValue: number,
  ) => {
    return dontUseGlobalState ? localHook(defaultValue) : globalHook();
  };

  const [page, setPageParam] = useParamsState(usePageParam, useState, 0);
  const [pageSize, setPageSizeParam] = useParamsState(usePageSizeParam, useState, 10);

  return (
    <>
      <DataGrid
        loading={loadingItems}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        density="standard"
        {...props}
        columns={columns}
        rows={items}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
            display: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-row .MuiDataGrid-cellCheckbox .Mui-disabled ': {
            display: 'none',
          },
          ...props.sx,
        }}
        slots={{
          noRowsOverlay: EmptyTableOverlay,
          ...props.slots,
        }}
      />

      {showPagination && (
        <TablePagination
          component="div"
          className={twMerge('absolute bottom-0 right-0', props.paginationClassName)}
          count={itemsCount}
          page={withDefault(page, 0)}
          rowsPerPage={withDefault(pageSize, 0)}
          onPageChange={(_event, newPage) => {
            setPageParam(newPage);
          }}
          onRowsPerPageChange={(event) => {
            setPageSizeParam(Number(event.target.value));
            setPageParam(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}
    </>
  );
}